import { createContext, useContext, useEffect, useState } from 'react';
import { getCurrentTenant } from '../utils/tenantDetection';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState(null);
  const [isTenantMode, setIsTenantMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Detect tenant from hostname or path on component mount
    const tenant = getCurrentTenant();
    const tenantMode = tenant !== null;
    
    setCurrentTenant(tenant);
    setIsTenantMode(tenantMode);
    setLoading(false);
    
    // Log for debugging
    if (tenant) {
      console.log(`🏢 [TenantContext] Detected tenant: ${tenant}`);
      console.log(`🌐 [TenantContext] Tenant mode: ${tenantMode}`);
    }
  }, []);

  /**
   * Get tenant-specific API base URL
   * @returns {string} - API base URL for current tenant
   */
  const getTenantApiUrl = () => {
    if (!currentTenant) return 'https://ecommerce.bigfat.ai/api';
    return `https://ecommerce.bigfat.ai/api`;
  };

  /**
   * Get tenant-specific headers for API requests
   * @returns {Object} - Headers object with tenant info
   */
  const getTenantHeaders = () => {
    const headers = {};
    if (currentTenant) {
      headers['X-Tenant-ID'] = currentTenant;
    }
    return headers;
  };

  /**
   * Check if current user can access tenant-specific features
   * @param {string} userRole - User's role
   * @returns {boolean} - Whether user has access
   */
  const canAccessTenantFeatures = (userRole) => {
    // In tenant mode, restrict access based on role
    if (isTenantMode) {
      return ['admin', 'user'].includes(userRole);
    }
    // In platform mode, only superadmin can access
    return userRole === 'superadmin';
  };

  /**
   * Get appropriate redirect path based on tenant context
   * @param {string} userRole - User's role
   * @returns {string} - Redirect path
   */
  const getRedirectPath = (userRole) => {
    if (isTenantMode) {
      // Tenant-specific routing
      switch (userRole) {
        case 'admin':
          return `/tenant/${currentTenant}`;
        case 'user':
          return `/tenant/${currentTenant}`;
        default:
          return '/login';
      }
    } else {
      // Platform routing
      switch (userRole) {
        case 'superadmin':
          return '/dashboard';
        default:
          return '/login';
      }
    }
  };

  const value = {
    currentTenant,
    isTenantMode,
    loading,
    getTenantApiUrl,
    getTenantHeaders,
    canAccessTenantFeatures,
    getRedirectPath,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};
